import { inject, Injectable } from '@angular/core';
import { Report } from '@app/core/model/client/report';
import ApiService from '@services/api.service';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class MyReportService {
  private readonly apiService = inject(ApiService);

  private readonly reportGraphqlFragment = gql`
    fragment ReportConfig on UserReport {
      id
      organizationId
      enabled
      lastSendDate
      weekday
      timeOfDay
      frequency
      assetConfig
      spaceConfig
      checkConfig
      occupantConfig
      leaseConfig
      contractConfig
      workConfig
      equipmentConfig
      projectConfig
      investmentConfig
      documentConfig
    }
  `;

  /**
   * Check if the current user report exists for the organization.
   * @param organizationId The ID of the organization.
   * @return Whether the report exists.
   */
  public doesUserReportExist(organizationId: string): Observable<boolean> {
    const query = gql`
      query DoesUserReportExist($organizationId: String!) {
        doesUserReportExist(organizationId: $organizationId)
      }
    `;
    const variables = {organizationId};

    return this.apiService.query({query, variables})
      .pipe(map(data => data['doesUserReportExist'] as boolean));
  }

  /**
   * Get user report by organization id
   * @param organizationId The organization Id
   * @returns The User report
   */
  public getReportByOrganizationId(organizationId: string): Observable<Report> {
    const query = gql`
      query UserReport($organizationId: String!) {
        userReport(organizationId: $organizationId) {
          ...ReportConfig
        }
      }${this.reportGraphqlFragment}
    `;
    const variables = {organizationId};
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Report, data['userReport'])));
  }

  /**
   * Send last report for the organization
   * @param organizationId the organization Id
   * @return If the report was sent successfully
   */
  public sendLastReport(organizationId: string): Observable<boolean> {
    const mutation = gql`
      mutation SendLastUserReport($organizationId: String!) {
        sendLastUserReport(organizationId: $organizationId)
      }
    `;
    const variables = {organizationId};

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => data['sendLastUserReport'] as boolean));
  }

  /**
   * Updates the user's organization report with a new configuration
   * @param reportData the new configuration
   * @return The updated report
   */
  public updateUserReport(reportData: any): Observable<Report> {
    reportData.reportInput.locale = navigator.language;
    const mutation = gql`
      mutation UpdateUserReport($reportId: Int!, $reportInput: UserReportInput!) {
        updateUserReport(reportId: $reportId, reportInput: $reportInput) {
          ...ReportConfig
        }
      }${this.reportGraphqlFragment}
    `;

    return this.apiService.mutate({mutation, variables: reportData})
      .pipe(map(data => plainToInstance(Report, data['updateUserReport'])));
  }

  /**
   * Create a user report configuration for the current user in the given organization.
   * @param organizationId The ID of the organization.
   * @param timezoneId The ID of the timezone.
   * @return The report.
   */
  public createReport(organizationId: string, timezoneId: string): Observable<Report> {
    const mutation = gql`
      mutation CreateUserReport($organizationId: String!, $timezoneId: String!, $locale: String!) {
        createUserReport(organizationId: $organizationId, timezoneId: $timezoneId, locale: $locale) {
          ...ReportConfig
        }
      }${this.reportGraphqlFragment}
    `;
    const variables = {organizationId, timezoneId, locale: navigator.language};
    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Report, data['createUserReport'])));
  }
}
